import * as RT from '@radix-ui/react-tabs'
import { useEffect, useRef, type ComponentProps, type Ref } from 'react'
import { revealInScroller } from '@/lib/scroll'
import { cn } from './cn'

export const Tabs = RT.Root

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node)
  else if (ref) (ref as { current: T | null }).current = node
}

/**
 * Underlined tab row; scrolls horizontally when it overflows. The selected tab is scrolled into view on mount,
 * whenever the selection changes (tap, Back, shared link) and when the tabs change width (web font, counts
 * loading), so it never sits clipped at the edge on phones (Q-SRCH-35). A tab reached with Tab is moved clear
 * of the edges too.
 */
export function TabsList({ className, onFocus, ref, ...rest }: ComponentProps<typeof RT.List>) {
  const listRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const revealActive = () => {
      const active = el.querySelector<HTMLElement>('[role="tab"][data-state="active"]')
      if (active) revealInScroller(el, active, { center: true })
    }
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(revealActive)
    const watchTabs = () => {
      if (!ro) return
      ro.observe(el)
      el.querySelectorAll<HTMLElement>('[role="tab"]').forEach((tab) => ro.observe(tab))
    }
    revealActive()
    watchTabs()
    const mo =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver((records) => {
            // Only selection changes on the triggers themselves, or tabs added and removed, not a tooltip inside a tab.
            const relevant = records.some((r) => r.type === 'childList' || (r.target as Element).getAttribute('role') === 'tab')
            if (!relevant) return
            watchTabs()
            revealActive()
          })
    // data-state flips on the triggers when the selection changes; childList catches tabs and counts added later.
    mo?.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-state'] })
    return () => {
      mo?.disconnect()
      ro?.disconnect()
    }
  }, [])

  return (
    <RT.List
      ref={(node: HTMLDivElement | null) => {
        listRef.current = node
        assignRef(ref, node)
      }}
      onFocus={(e) => {
        onFocus?.(e)
        if (e.target instanceof HTMLElement && e.target !== e.currentTarget) revealInScroller(e.currentTarget, e.target)
      }}
      className={cn('scrollbar-none flex items-end gap-1 overflow-x-auto border-b border-line', className)}
      {...rest}
    />
  )
}

export function TabsTrigger({ className, children, ...rest }: ComponentProps<typeof RT.Trigger>) {
  return (
    <RT.Trigger
      className={cn(
        'group relative inline-flex h-10 shrink-0 items-center gap-1.5 rounded-t-sm px-3 text-tab font-medium whitespace-nowrap text-subtle',
        'transition-colors duration-150 ease-out hover:text-text focus-ring data-[state=active]:font-semibold data-[state=active]:text-text',
        'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4',
        className,
      )}
      {...rest}
    >
      <span className="grid">
        <span aria-hidden="true" className="invisible col-start-1 row-start-1 inline-flex items-center gap-1.5 font-semibold">
          {children}
        </span>
        <span className="col-start-1 row-start-1 inline-flex items-center justify-center gap-1.5">{children}</span>
      </span>
      <span
        aria-hidden="true"
        className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-[3px] bg-text opacity-0 transition-opacity duration-150 group-data-[state=active]:opacity-100"
      />
    </RT.Trigger>
  )
}

export function TabsContent({ className, ...rest }: ComponentProps<typeof RT.Content>) {
  return <RT.Content className={cn('pt-4 outline-none', className)} {...rest} />
}
